import React, { useState } from "react";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip } from "../ui/tooltip";
import { Plus, Info } from "lucide-react";
import NewApplicationModal from "../Application/NewApplicationModal";

interface AgentBasicInfoProps {
  agent: Partial<Agent>;
  setAgent: (agent: Partial<Agent>) => void;
  applications: Application[];
  isLoading: boolean;
  onApplicationCreated?: (application: Application) => void;
}

const AgentBasicInfo: React.FC<AgentBasicInfoProps> = ({
  agent,
  setAgent,
  applications,
  isLoading,
  onApplicationCreated
}) => {
  const [showNewAppModal, setShowNewAppModal] = useState(false);
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: any) => {
    setAgent({
      ...agent,
      [field]: value
    });

    // Validate base_url on change
    if (field === "base_url") {
      if (value && value.trim()) {
        try {
          new URL(value);
          setBaseUrlError(null);
        } catch (error) {
          setBaseUrlError("Please enter a valid URL (e.g., https://api.example.com)");
        }
      } else {
        setBaseUrlError(null);
      }
    }
  };

  const handleApplicationSelect = (value: string) => {
    if (value === "new_application") {
      setShowNewAppModal(true);
    } else {
      const parsedId = parseInt(value);
      if (!isNaN(parsedId)) {
        handleInputChange("application_id", parsedId);
      }
    }
  };

  const handleApplicationCreated = (newApplication: Application) => {
    // Automatically select the newly created application
    handleInputChange("application_id", newApplication.id);
    // Notify parent to refresh applications list
    if (onApplicationCreated) {
      onApplicationCreated(newApplication);
    }
  };

  return (
    <div className="space-y-6">
      {/* Agent Name */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="agent_name">Agent Name</Label>
          <Tooltip content="Give your agent a clear, descriptive name that indicates its primary function or role." immediate>
            <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
          </Tooltip>
        </div>
        <Input
          id="agent_name"
          type="text"
          placeholder="Customer Support Agent"
          value={agent.agent_name || ""}
          onChange={(e) => handleInputChange("agent_name", e.target.value)}
          className="w-full"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="description">Description</Label>
          <Tooltip content="Explain what this agent does and when it should be used. This helps determine how user requests are handled." immediate>
            <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
          </Tooltip>
        </div>
        <Textarea
          id="description"
          placeholder="This agent is a customer support assistant and is to be used for all general customer support inquiries. It can handle questions about products, orders, returns, and general troubleshooting."
          value={agent.description || ""}
          onChange={(e) => handleInputChange("description", e.target.value)}
          className="w-full min-h-[120px]"
        />
      </div>

      {/* Base URL */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="base_url">Base URL (Optional)</Label>
          <Tooltip content="Default base URL for all API actions. Individual actions can override this URL if needed. Leave empty to use full URLs in action configurations.">
            <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
          </Tooltip>
        </div>
        <Input
          id="base_url"
          type="url"
          placeholder="https://api.example.com"
          value={(agent as any).base_url || ""}
          onChange={(e) => handleInputChange("base_url", e.target.value)}
          className="w-full"
        />
        {baseUrlError && (
          <p className="text-sm text-red-500 mt-1">{baseUrlError}</p>
        )}
      </div>

      {/* Application Selection */}
      <div className="space-y-2">
        <Label htmlFor="application">Application</Label>
        <Select
          value={agent.application_id?.toString() || ""}
          onValueChange={handleApplicationSelect}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select the application this agent belongs to" />
          </SelectTrigger>
          <SelectContent>
            {applications
              .filter((app) => app.id != null && !isNaN(app.id))
              .map((app) => (
                <SelectItem key={app.id} value={app.id!.toString()}>
                  {app.application_name}
                </SelectItem>
              ))}
            <SelectItem value="new_application" className="border-t border-border mt-1 pt-2">
              <div className="flex items-center gap-2 text-maitai-green font-medium">
                <Plus className="w-4 h-4" />
                Create New Application
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* New Application Modal */}
      <NewApplicationModal
        isOpen={showNewAppModal}
        onClose={() => setShowNewAppModal(false)}
        onApplicationCreated={handleApplicationCreated}
      />
    </div>
  );
};

export default AgentBasicInfo; 